import type { BranchDto, ReportExportColumn, ReportExportRequest } from '@sellokart/shared';

/**
 * One exportable column: the server's column shape plus the on-screen
 * column's `key` (so the user's Columns layout picks and orders it) and a
 * plain-text cell — the export carries strings, never React nodes.
 */
export interface ExportColumnSpec<T> extends ReportExportColumn {
  key: string;
  text: (row: T) => string;
}

/** "2026-10-01" → "01 Oct 2026"; anything unparseable is shown as given. */
function readableDate(value: string): string {
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * "01 Oct 2026 – 16 Oct 2026 · Main branch". With no range picked the API
 * falls back to its own default window, which the page never sees — so the
 * subtitle says so rather than inventing dates.
 */
export function exportSubtitle(
  range: { from: string; to: string },
  branch?: { branchId: string; branches: readonly BranchDto[] },
): string {
  const parts = [
    range.from && range.to ? `${readableDate(range.from)} – ${readableDate(range.to)}` : 'Last 30 days',
  ];
  if (branch) {
    const name = branch.branches.find((b) => b.id === branch.branchId)?.name;
    parts.push(branch.branchId ? (name ?? 'Selected branch') : 'All branches');
  }
  return parts.join(' · ');
}

/** The server refuses a cell over 500 characters — a long note is cut, not the whole export. */
const clamp = (text: string) => (text.length > 500 ? `${text.slice(0, 499)}…` : text);

/**
 * The export of a table as shown: only the columns the layout shows, in its
 * order (`visibleKeys` — the keys of `applyReportLayout`'s result; omit for a
 * fixed table). Totals are keyed by column like DataTable's: the renderer
 * puts `label` in the first column, so `cells` covers the rest.
 */
export function buildExportTable<T>(input: {
  title: string;
  subtitle: string;
  from?: string;
  to?: string;
  columns: ReadonlyArray<ExportColumnSpec<T>>;
  visibleKeys?: readonly string[];
  rows: readonly T[];
  totals?: { label: string; cells: Record<string, string> };
  notes?: string[];
}): ReportExportRequest {
  const byKey = new Map(input.columns.map((column) => [column.key, column]));
  const specs = input.visibleKeys
    ? input.visibleKeys.map((key) => byKey.get(key)).filter((c): c is ExportColumnSpec<T> => c !== undefined)
    : [...input.columns];
  const columns: ReportExportColumn[] = specs.map(({ key: _key, text: _text, ...column }) => column);
  const totals = input.totals;
  return {
    title: input.title,
    subtitle: input.subtitle,
    from: input.from && input.to ? input.from : '',
    to: input.from && input.to ? input.to : '',
    columns,
    rows: input.rows.map((row) => specs.map((spec) => clamp(spec.text(row)))),
    ...(totals && specs.length > 0
      ? { totals: { label: totals.label, cells: specs.slice(1).map((spec) => totals.cells[spec.key] ?? '') } }
      : {}),
    ...(input.notes?.length ? { notes: input.notes } : {}),
  };
}
